import { uniqBy } from 'lodash'
import React, { FC, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { ReactSVG } from 'react-svg'
import { RootState } from '../../../../../states/containers/store'
import { ResponseMonthlyTeamReport, ResponseMonthlyMemberReport } from '../../../../../states/types/report'
import { buildWorkTime, nowDate, secToHour } from '../../../../../utils/date'
import Loading from '../../../common/Loading'

interface List {
  title: string
  color: string | null
  icon: string | null
  icon_color: string | null
  past: {
    time: number
    biz: number
  },
  current: {
    time: number
    biz: number
  },
}

interface Label {
  name: string
  biz: number
  time: number
  color: string | null
  icon_color: string | null
  icon: string | null
}

interface GraphList {
  title: string
  value: number
  fill: string
}

interface Search {
  startDate: string
  endDate: string
  teamIds: number[]
  teamNames: string[]
  memberIds: number[]
  memberNames: string[]
  keywords: string[]
  serviceNames: string[]
  categoryIds: number[]
  categoryNames: string[]
  efficiencyLevel: string[]
  stressLevel: string[]
  focusLevel: string[]
  sortKey?: string,
  sortType?: string,
}

interface Props {
  target: string
  searchModalOpenCallback: (search: Search) => void
}

const MonthlyApplication: FC<Props> = (props) => {
  const {
    target,
    searchModalOpenCallback,
  } = props

  const {
    report,
  } = useSelector((state: RootState) => (state))

  const tipCurrentRef = useRef<HTMLDivElement>(null)
  const tipPastRef = useRef<HTMLDivElement>(null)

  const [isDisplayCurrentTip, setIsDisplayCurrentTip] = useState<boolean>(false)
  const [isDisplayPastTip, setIsDisplayPastTip] = useState<boolean>(false)

  const [appList, setAppList] = useState<List[]>([])

  const [timeMaxValue, setTimeMaxValue] = useState<number>(0)
  const [bizMaxValue, setBizMaxValue] = useState<number>(0)

  const [isDisplayTime, setIsDisplayTime] = useState<boolean>(true)

  const [focusTitle, setFocusTitle] = useState<string>("")

  const [reportData, setReportData] = useState<ResponseMonthlyTeamReport | ResponseMonthlyMemberReport>()

  const [isDisplayComment, setIsDisplayComment] = useState<boolean>(true)

  const [labelList, setLabelList] = useState<Label[]>([])

  const brightness = (rgb: string) => (
    (parseInt(rgb.substring(1, 3), 16) * 0.299)
    + (parseInt(rgb.substring(3, 5), 16) * 0.587)
    + (parseInt(rgb.substring(5, 7), 16) * 0.114)
  )

  const displayTip = (data: GraphList, angle: number, sum: number, type?: string) => {
    if (!tipCurrentRef.current || !tipPastRef.current) return
    if (type === "current") {
      tipCurrentRef.current.style.left = `${200 + (150 * Math.cos((sum + (angle / 2) - 90) * (Math.PI / 180)))}px`
      tipCurrentRef.current.style.top = `${200 + (150 * Math.sin((sum + (angle / 2) - 90) * (Math.PI / 180)))}px`
      tipCurrentRef.current.style.backgroundColor = `${data.fill}`
      tipCurrentRef.current.style.color = brightness(data.fill) >= 140 ? '#000000' : '#FFFFFF'
      if (isDisplayTime) {
        tipCurrentRef.current.textContent = `${data.title} : ${buildWorkTime(data.value || 0)}`
      } else {
        tipCurrentRef.current.textContent = `${data.title} : ${data.value}`
      }
      setIsDisplayCurrentTip(true)
    } else {
      tipPastRef.current.style.left = `${200 + (120 * Math.cos((sum + (angle / 2) - 90) * (Math.PI / 180)))}px`
      tipPastRef.current.style.top = `${150 + (120 * Math.sin((sum + (angle / 2) - 90) * (Math.PI / 180)))}px`
      tipPastRef.current.style.backgroundColor = `${data.fill}`
      tipPastRef.current.style.color = brightness(data.fill) >= 140 ? '#000000' : '#FFFFFF'
      if (isDisplayTime) {
        tipPastRef.current.textContent = `${data.title} : ${buildWorkTime(data.value || 0)}`
      } else {
        tipPastRef.current.textContent = `${data.title} : ${data.value}`
      }
      setIsDisplayPastTip(true)
    }
  }

  const hiddenTip = () => {
    setIsDisplayCurrentTip(false)
    setIsDisplayPastTip(false)
  }

  const closeScreen = () => {
    hiddenTip()
    setFocusTitle("")
  }

  const d = (cx: number, cy: number, r: number, a: number, s: number) => {
    const rad1 = ((s - 90) / 180) * Math.PI
    const rad2 = ((a + s - 90) / 180) * Math.PI
    const params = [
      cx + r * Math.cos(rad1), cy + r * Math.sin(rad1),
      r, r,
      a > 180 ? 1 : 0,
      cx + r * Math.cos(rad2), cy + r * Math.sin(rad2),
      cx, cy,
    ]

    return params.reduce((str, param) => str.replace('%d', String(param)), 'M %d,%d A %d,%d 0 %d,1 %d,%d L %d,%d Z')
  }

  const clickArcArea = (title: string) => {
    if (focusTitle === title) {
      setFocusTitle("")
    } else {
      setFocusTitle(title)
    }
  }

  const arc = (svg: HTMLElement | null, cx: number, cy: number, r: number, list: GraphList[], type?: string, focus?: string) => {
    if (!svg) return
    svg.innerHTML = ""
    let total = 0
    let s = 0

    if (!list.length) {
      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path')
      const a = 359.999

      path.setAttribute('d', d(cx, cy, r, a, s))
      path.style.opacity = "1"
      path.setAttribute('fill', "#ECF0F5")
      path.setAttribute('stroke', '#fff')
      svg.appendChild(path)
      return
    }

    list.forEach((data: GraphList) => {
      total += data.value
    })
    list.forEach((data: GraphList) => {
      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path')
      const a = (data.value / total) * 360
      const sum = s

      if (data.title === focus) {
        path.setAttribute('d', d(cx, cy, r * 1.05, a, s))
        path.style.opacity = "1"
      } else {
        path.setAttribute('d', d(cx, cy, r, a, s))
        if (focus) {
          path.style.opacity = "0.1"
        } else {
          path.style.opacity = "1"
        }
      }
      path.setAttribute('fill', data.fill)
      path.setAttribute('stroke', '#fff')
      path.onclick = () => clickArcArea(data.title)
      path.onmouseover = () => displayTip(data, a, sum, type)
      path.onmouseout = () => hiddenTip()
      path.style.cursor = "pointer"
      svg.appendChild(path)
      s += a
    })
  }

  const outerLabel = (wrap: HTMLElement | null, list: GraphList[], type: string, focus?: string) => {
    if (!wrap) return
    wrap.innerHTML = ""
    let total = 0
    let sum = 0

    list.forEach((data: GraphList) => {
      total += data.value
    })
    list.forEach((data: GraphList) => {
      const div = document.createElement('div')
      const span = document.createElement('span')
      const percentage = (data.value / total) * 100
      if (percentage < 3) return
      const angle = percentage * 3.6
      const focusAdd = data.title === focus ? 10 : 0
      const fontSize = data.title === focus ? 24 : 18

      span.textContent = `${Math.trunc(percentage)}`
      span.style.fontSize = `${fontSize}px`

      div.style.position = "absolute"
      div.style.fontSize = "10px"
      div.style.color = "#6E6D6D"
      div.style.transform = "translateX(-50%) translateY(-50%)"
      if (type === "current") {
        div.style.top = `${200 + ((170 + focusAdd) * Math.sin((sum + (angle / 2) - 90) * (Math.PI / 180)))}px`
        div.style.left = `${200 + ((170 + focusAdd) * Math.cos((sum + (angle / 2) - 90) * (Math.PI / 180)))}px`
      } else {
        div.style.top = `${150 + ((140 + focusAdd) * Math.sin((sum + (angle / 2) - 90) * (Math.PI / 180)))}px`
        div.style.left = `${200 + ((140 + focusAdd) * Math.cos((sum + (angle / 2) - 90) * (Math.PI / 180)))}px`
      }
      div.innerHTML = `${span.outerHTML}%`

      wrap.appendChild(div)
      sum += angle
    })
  }

  const switchDisplay = (displayTime: boolean) => {
    setFocusTitle("")
    setIsDisplayTime(displayTime)
  }

  useEffect(() => {
    let pastList:GraphList[] = []
    let currentList:GraphList[] = []
    let tmpPastSortList = []
    let tmpCurrentSortList = []
    if (isDisplayTime) {
      tmpPastSortList = reportData?.service_work_comparisons?.past.time.data || []
      pastList = tmpPastSortList.map((value) => ({ title: value.name, value: value.time, fill: value?.color || "#CCC" }))
      tmpCurrentSortList = reportData?.service_work_comparisons?.current.time.data || []
      currentList = tmpCurrentSortList.map((value) => ({ title: value.name, value: value.time, fill: value?.color || "#CCC" }))
    } else {
      tmpPastSortList = reportData?.service_work_comparisons?.past.biz.data || []
      pastList = tmpPastSortList.map((value) => ({ title: value.name, value: value.biz, fill: value?.color || "#CCC" }))
      tmpCurrentSortList = reportData?.service_work_comparisons?.current.biz.data || []
      currentList = tmpCurrentSortList.map((value) => ({ title: value.name, value: value.biz, fill: value?.color || "#CCC" }))
    }
    arc(document.getElementById('svg_app_past'), 200, 150, 120, pastList, "past", focusTitle)
    outerLabel(document.getElementById('label_app_past'), pastList, "past", focusTitle)
    arc(document.getElementById('svg_app_current'), 200, 200, 150, currentList, "current", focusTitle)
    outerLabel(document.getElementById('label_app_current'), currentList, "current", focusTitle)
  }, [isDisplayTime, focusTitle, reportData])

  useEffect(() => {
    if (!reportData?.service_work_comparisons) return
    setTimeMaxValue(Math.max(...reportData.service_work_comparisons.current.time.data.map((val) => val.time)))
    setBizMaxValue(Math.max(...reportData.service_work_comparisons.current.biz.data.map((val) => val.biz)))
  }, [reportData])

  useEffect(() => {
    if (!reportData?.service_work_comparisons) return
    let dataList: {
      name: string;
      biz: number;
      time: number;
      color: string | null;
      icon_color: string | null;
      icon: string | null;
    }[] = []
    let pastDataList: {
      name: string;
      biz: number;
      time: number;
      color: string | null;
      icon_color: string | null;
      icon: string | null;
    }[] = []
    if (isDisplayTime) {
      dataList = reportData.service_work_comparisons.current.time.data
      pastDataList = reportData.service_work_comparisons.past.time.data
    } else {
      dataList = reportData.service_work_comparisons.current.biz.data
      pastDataList = reportData.service_work_comparisons.past.biz.data
    }

    setAppList(
      dataList.map((data) => (
        {
          title: data.name,
          color: data.color,
          icon: data.icon,
          icon_color: data.icon_color,
          past: {
            time: pastDataList.find((value) => value.name === data.name)?.time || 0,
            biz: pastDataList.find((value) => value.name === data.name)?.biz || 0,
          },
          current: {
            time: dataList.find((value) => value.name === data.name)?.time || 0,
            biz: dataList.find((value) => value.name === data.name)?.biz || 0,
          },
        }
      )),
    )

    setLabelList(uniqBy([...dataList, ...pastDataList], 'name'))
  }, [isDisplayTime, reportData])

  useEffect(() => {
    if (!target) return
    if (target === "team") {
      if (!report.monthlyTeamReport) return
      setReportData(report.monthlyTeamReport)
    } else {
      if (!report.monthlyMemberReport) return
      setReportData(report.monthlyMemberReport)
    }
  }, [target, report])

  return (
    <>
      {
        report.isLoading ? (
          <>
            <Loading />
          </>
        ) : (
          <>
            <section id="monthly_application" className="page_contents">
              <div className="content-block monthly">
                <div className="block-header">
                  <div className="title">
                    アプリ別 作業比較
                  </div>
                </div>
                <div className="block-detail">
                  <div className="comparison">
                    <div className="left_area">
                      <div className="header_area">
                        <div className="text">
                          先月との比較
                        </div>
                        <div className="bar" />
                        <div className="switch_area">
                          <div
                            className={`left ${isDisplayTime ? "active" : ""}`}
                            onClick={() => switchDisplay(true)}
                            role="none"
                          >
                            時間
                          </div>
                          <div
                            className={`right ${isDisplayTime ? "" : "active"}`}
                            onClick={() => switchDisplay(false)}
                            role="none"
                          >
                            Biz
                          </div>
                        </div>
                      </div>
                      <div className="past_graph">
                        <svg xmlns="http://www.w3.org/2000/svg" version="1.1" width="400" height="300" id="svg_app_past" />
                        <div className="display_title">
                          先月
                        </div>
                        <div id="label_app_past" />
                        <div
                          className="display_tip"
                          style={{
                            opacity: `${isDisplayPastTip ? 1 : 0}`,
                          }}
                          ref={tipPastRef}
                        />
                      </div>
                      <div className="current_graph">
                        <svg xmlns="http://www.w3.org/2000/svg" version="1.1" width="400" height="400" id="svg_app_current" />
                        <div className="display_title">
                          今月
                        </div>
                        <div id="label_app_current" />
                        <div
                          className="display_tip"
                          style={{
                            opacity: `${isDisplayCurrentTip ? 1 : 0}`,
                          }}
                          ref={tipCurrentRef}
                        />
                      </div>
                    </div>
                    <div className="right_area">
                      <div className="header_area">
                        <div className="text">
                          時間と量の比較
                        </div>
                        <div className="bar" />
                      </div>
                      {
                        appList.map((list) => (
                          <div
                            className="item"
                            key={`list_${list.title}`}
                          >
                            {
                              isDisplayTime
                              && reportData?.service_work_comparisons?.current.time.comment.key === list.title
                              && secToHour(Math.abs(reportData?.service_work_comparisons?.current.time.comment.fluctuation)) > 0
                              && (
                                <>
                                  <ReactSVG
                                    src="/static/images/icon/icon-warn.svg"
                                    className="icon_warn"
                                    onClick={() => setIsDisplayComment(!isDisplayComment)}
                                  />
                                  {
                                    isDisplayComment && (
                                      <div className="comment_wrap">
                                        <div className="comment">
                                          {list.title}
                                          が前月に比べて
                                          {secToHour(Math.abs(reportData?.service_work_comparisons?.current.time.comment.fluctuation))}
                                          時間
                                          {
                                            reportData?.service_work_comparisons?.current.time.comment.fluctuation > 0 ? (
                                              <span>
                                                増加
                                              </span>
                                            ) : (
                                              <span>
                                                減少
                                              </span>
                                            )
                                          }
                                          しました。
                                          <div
                                            className="close"
                                            role="none"
                                            onClick={() => setIsDisplayComment(false)}
                                          >
                                            ×
                                          </div>
                                        </div>
                                      </div>
                                    )
                                  }
                                </>
                              )
                            }
                            {
                              !isDisplayTime
                              && reportData?.service_work_comparisons?.current.biz.comment.key === list.title
                              && secToHour(Math.abs(reportData?.service_work_comparisons?.current.biz.comment.fluctuation)) > 0
                              && (
                                <>
                                  <ReactSVG
                                    src="/static/images/icon/icon-warn.svg"
                                    className="icon_warn"
                                    onClick={() => setIsDisplayComment(!isDisplayComment)}
                                  />
                                  {
                                    isDisplayComment && (
                                      <div className="comment_wrap">
                                        <div className="comment">
                                          {list.title}
                                          が前月に比べて
                                          {Math.abs(reportData?.service_work_comparisons?.current.biz.comment.fluctuation)}
                                          Biz
                                          {
                                            reportData?.service_work_comparisons?.current.biz.comment.fluctuation > 0 ? (
                                              <span>
                                                増加
                                              </span>
                                            ) : (
                                              <span>
                                                減少
                                              </span>
                                            )
                                          }
                                          しました。
                                          <div
                                            className="close"
                                            role="none"
                                            onClick={() => setIsDisplayComment(false)}
                                          >
                                            ×
                                          </div>
                                        </div>
                                      </div>
                                    )
                                  }
                                </>
                              )
                            }
                            <div
                              className="icon"
                              style={{
                                backgroundColor: list.color || "#CCC",
                                opacity: !focusTitle || focusTitle === list.title ? 1 : 0.1,
                              }}
                              onClick={() => clickArcArea(list.title)}
                              role="none"
                            >
                              <i
                                className={`${list.icon}`}
                                style={{
                                  color: list.icon_color || "#000",
                                  width: "24px",
                                  height: "24px",
                                }}
                              />
                            </div>
                            <div className="text_area">
                              <div className="title">
                                {list.title === "other" ? "その他" : list.title}
                              </div>
                              <div className="button_area">
                                {
                                  list.title !== "other" && reportData && "team" in reportData && (
                                    <button
                                      data-gtm-click="application - detail - button"
                                      type="button"
                                      className="link_button"
                                      onClick={() => searchModalOpenCallback(
                                        {
                                          startDate: reportData?.date?.from || nowDate('YYYY-MM-DD'),
                                          endDate: reportData?.date?.to || nowDate('YYYY-MM-DD'),
                                          teamIds: [reportData?.team?.id || 0],
                                          teamNames: [reportData?.team?.name || ""],
                                          memberIds: [],
                                          memberNames: [],
                                          keywords: [],
                                          serviceNames: [list.title],
                                          categoryIds: [],
                                          categoryNames: [],
                                          efficiencyLevel: [],
                                          stressLevel: [],
                                          focusLevel: [],
                                          sortKey: isDisplayTime ? "time" : "biz",
                                          sortType: "desc",
                                        },
                                      )}
                                    >
                                      詳細を見る
                                    </button>
                                  )
                                }
                                {
                                  list.title !== "other" && reportData && "member" in reportData && (
                                    <button
                                      data-gtm-click="application - detail - button"
                                      type="button"
                                      className="link_button"
                                      onClick={() => searchModalOpenCallback(
                                        {
                                          startDate: reportData?.date?.from || nowDate('YYYY-MM-DD'),
                                          endDate: reportData?.date?.to || nowDate('YYYY-MM-DD'),
                                          teamIds: [],
                                          teamNames: [],
                                          memberIds: [reportData?.member?.id || 0],
                                          memberNames: [reportData?.member?.name || ""],
                                          keywords: [],
                                          serviceNames: [list.title],
                                          categoryIds: [],
                                          categoryNames: [],
                                          efficiencyLevel: [],
                                          stressLevel: [],
                                          focusLevel: [],
                                          sortKey: isDisplayTime ? "time" : "biz",
                                          sortType: "desc",
                                        },
                                      )}
                                    >
                                      詳細を見る
                                    </button>
                                  )
                                }
                              </div>
                            </div>
                            <div
                              className="bar_graph_wrap"
                              style={{
                                opacity: !focusTitle || focusTitle === list.title ? 1 : 0.1,
                              }}
                            >
                              <div className="bar_graph_item">
                                <div className="title">
                                  時間
                                </div>
                                <div className={`bar_graph_area ${isDisplayTime && "active"}`}>
                                  <div
                                    className="bar_graph"
                                    style={{
                                      width: `${(list.current.time / timeMaxValue) * 100}%`,
                                    }}
                                  >
                                    <div className="bar_graph_text">
                                      {Math.floor(list.current.time / 3600)}
                                      :
                                      {`00${Math.floor((list.current.time % 3600) / 60)}`.slice(-2)}
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="bar_graph_item">
                                <div className="title">
                                  Biz
                                </div>
                                <div className={`bar_graph_area ${!isDisplayTime && "active"}`}>
                                  <div
                                    className="bar_graph"
                                    style={{
                                      width: `${(list.current.biz / bizMaxValue) * 100}%`,
                                    }}
                                  >
                                    <div className="bar_graph_text">
                                      {list.current.biz.toLocaleString()}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))
                      }
                    </div>
                  </div>
                  <div className="label_area">
                    {
                      labelList.map((list) => (
                        list.name !== "other" && (
                          <div
                            className="label_item"
                            style={{
                              opacity: !focusTitle || focusTitle === list.name ? 1 : 0.1,
                            }}
                            onClick={() => clickArcArea(list.name)}
                            role="none"
                            key={`label_${list.name}`}
                          >
                            <div
                              className="label_color"
                              style={{
                                backgroundColor: list.color || "#CCC",
                              }}
                            />
                            <div className="label_text">
                              {list.name}
                            </div>
                          </div>
                        )
                      ))
                    }
                  </div>
                </div>
              </div>
            </section>
            <div className={`close_screen ${focusTitle || 'none'}`} role="presentation" onClick={() => closeScreen()} />
          </>
        )
      }
    </>
  )
}

export default MonthlyApplication
